/* eslint-disable react/jsx-no-constructed-context-values */
// src/contexts/ProgramCreationContext.tsx
import { createContext, ReactNode, useContext, useReducer, useEffect, useRef } from 'react';
import { API } from '../../../api';
import { contentResponseToManualModules } from './masterclass/ai/mappers';
import { ModuleDraft } from './masterclass/types';

type ProgramType = 'masterclass' | 'mentorship' | 'guided-masterclass' | null;
type Module = ModuleDraft;

interface DocumentItem {
  id: string;
  name: string;
  type: 'file' | 'text' | 'url';
  status: 'processing' | 'ready';
}

interface MasterclassDraft {
  title?: string;
  description?: string;
  documents?: File[];
  outlineConfig?: any;
  generationId?: string;
}

interface MentorshipDraft {
  name?: string;
  learningObjectives?: string[];
  matchSettings?: {
    maxMatchesPerMentee: number;
    maxMatchesPerMentor: number;
    sessionsPerMatch: number;
  };
  mentors?: string[];
  notes?: string;
}

interface GuidedMasterclassDraft {
  schedule?: any[];
  supportMentors?: string[];
}

interface ProgramCreationState {
  programType: ProgramType;
  masterclass: MasterclassDraft;
  mentorship: MentorshipDraft;
  guidedMasterclass: GuidedMasterclassDraft;
  buildMode?: 'ai' | 'manual' | null;
  modules?: Module[];
  groupId?: string | null;
  generationId?: string | null;

  ai: {
    step: number;
    purpose: { name: string; description: string };
    objectives: string[];
    documents: DocumentItem[];
    pendingDocs: DocumentItem[];
    selectedDocIds: string[];
    reviewData: any;
    members: any;
    structure?: any;
    content?: any;
    structureLoading?: boolean;
    contentLoading?: boolean;
    contentStatus?: any;
    contentProgress?: { completedLessons?: number; totalLessons?: number; percentage?: number } | null;
    contentPollingActive?: boolean;
  };
}

type Action =
  | { type: 'SET_PROGRAM_TYPE'; payload: ProgramType }
  | { type: 'SET_BUILD_MODE'; payload: 'ai' | 'manual' }
  | { type: 'UPDATE_MASTERCLASS'; payload: Partial<MasterclassDraft> }
  | { type: 'UPDATE_MENTORSHIP'; payload: Partial<MentorshipDraft> }
  | { type: 'UPDATE_GUIDED'; payload: Partial<GuidedMasterclassDraft> }
  | { type: 'SET_AI_STEP'; payload: number }
  | { type: 'UPDATE_AI_PURPOSE'; payload: { name: string; description: string } }
  | { type: 'UPDATE_AI_OBJECTIVES'; payload: string[] }
  | { type: 'UPDATE_AI_REVIEW_DATA'; payload: any }
  | { type: 'UPDATE_AI_MEMBERS'; payload: any }
  | { type: 'ADD_PENDING_DOC'; payload: DocumentItem }
  | { type: 'UPDATE_DOC_STATUS'; payload: { id: string; status: 'ready' } }
  | { type: 'SET_SELECTED_DOCS'; payload: string[] }
  | { type: 'SET_MODULES'; payload: Module[] }
  | { type: 'UPDATE_MODULE'; payload: Module }
  | { type: 'DELETE_MODULE'; payload: string }
  | { type: 'SET_GROUP_ID'; payload: string }
  | { type: 'SET_GENERATION_ID'; payload: string }
  | { type: 'SET_AI_STRUCTURE'; payload: any }
  | { type: 'SET_AI_CONTENT'; payload: any }
  | { type: 'SET_AI_STRUCTURE_LOADING'; payload: boolean }
  | { type: 'SET_AI_CONTENT_LOADING'; payload: boolean }
  | { type: 'SET_AI_CONTENT_STATUS'; payload: any }
  | { type: 'SET_AI_CONTENT_PROGRESS'; payload: { completedLessons?: number; totalLessons?: number; percentage?: number } | null }
  | { type: 'SET_AI_CONTENT_POLLING'; payload: boolean }
  | { type: 'RESET' }
  | { type: 'RESET_KEEP_PROGRAM_TYPE'; payload?: { programType?: ProgramType } };

const initialState: ProgramCreationState = {
  programType: null,
  masterclass: {},
  mentorship: {},
  guidedMasterclass: {},
  buildMode: null,
  ai: {
    step: 1,
    purpose: { name: '', description: '' },
    objectives: ['', '', '', '', ''],
    reviewData: null,
    members: [],
    documents: [],
    pendingDocs: [],
    selectedDocIds: [],
    structure: null,
    content: null,
    structureLoading: false,
    contentLoading: false,
    contentStatus: null,
    contentProgress: null,
    contentPollingActive: false
  }
};

function reducer(state: ProgramCreationState, action: Action): ProgramCreationState {
  switch (action.type) {
    case 'SET_PROGRAM_TYPE':
      return { ...state, programType: action.payload };

    case 'SET_BUILD_MODE':
      return { ...state, buildMode: action.payload };

    case 'UPDATE_MASTERCLASS':
      return { ...state, masterclass: { ...state.masterclass, ...action.payload } };

    case 'UPDATE_MENTORSHIP':
      return { ...state, mentorship: { ...state.mentorship, ...action.payload } };

    case 'UPDATE_GUIDED':
      return { ...state, guidedMasterclass: { ...state.guidedMasterclass, ...action.payload } };

    // ✅ AI Flow Actions
    case 'SET_GROUP_ID':
      return { ...state, groupId: action.payload };

    case 'SET_GENERATION_ID':
      return { ...state, generationId: action.payload };

    case 'SET_AI_STEP':
      return { ...state, ai: { ...state.ai, step: action.payload } };

    case 'UPDATE_AI_PURPOSE':
      return { ...state, ai: { ...state.ai, purpose: action.payload } };

    case 'UPDATE_AI_OBJECTIVES':
      return { ...state, ai: { ...state.ai, objectives: action.payload } };

    case 'UPDATE_AI_REVIEW_DATA':
      return { ...state, ai: { ...state.ai, reviewData: action.payload } };

    case 'UPDATE_AI_MEMBERS':
      return { ...state, ai: { ...state.ai, members: action.payload } };

    case 'ADD_PENDING_DOC':
      return { ...state, ai: { ...state.ai, pendingDocs: [...state.ai.pendingDocs, action.payload] } };

    case 'UPDATE_DOC_STATUS': {
      const pendingDoc = state.ai.pendingDocs.find((d) => d.id === action.payload.id);
      if (!pendingDoc) return state; // segurança extra

      const readyDoc = { ...pendingDoc, status: 'ready' as const };

      return {
        ...state,
        ai: {
          ...state.ai,
          pendingDocs: state.ai.pendingDocs.filter((d) => d.id !== action.payload.id),
          documents: [...state.ai.documents, readyDoc]
        }
      };
    }

    case 'SET_SELECTED_DOCS':
      return { ...state, ai: { ...state.ai, selectedDocIds: action.payload } };

    case 'SET_AI_STRUCTURE':
      return { ...state, ai: { ...state.ai, structure: action.payload } };

    case 'SET_AI_CONTENT':
      return { ...state, ai: { ...state.ai, content: action.payload } };

    case 'SET_AI_STRUCTURE_LOADING':
      return { ...state, ai: { ...state.ai, structureLoading: action.payload } };

    case 'SET_AI_CONTENT_LOADING':
      return { ...state, ai: { ...state.ai, contentLoading: action.payload } };

    case 'SET_AI_CONTENT_STATUS':
      return { ...state, ai: { ...state.ai, contentStatus: action.payload } };

    case 'SET_AI_CONTENT_PROGRESS':
      return { ...state, ai: { ...state.ai, contentProgress: action.payload } };

    case 'SET_AI_CONTENT_POLLING':
      return { ...state, ai: { ...state.ai, contentPollingActive: action.payload } };

    case 'SET_MODULES':
      return { ...state, modules: action.payload };

    case 'UPDATE_MODULE':
      return {
        ...state,
        modules: state.modules?.map((m) => (m.id === action.payload.id ? action.payload : m))
      };

    case 'DELETE_MODULE':
      return {
        ...state,
        modules: state.modules?.filter((m) => m.id !== action.payload)
      };

    case 'RESET':
      return initialState;

    case 'RESET_KEEP_PROGRAM_TYPE': {
      const nextProgramType = action.payload?.programType ?? state.programType ?? null;
      return {
        ...initialState,
        programType: nextProgramType
      };
    }

    default:
      return state;
  }
}

const ProgramCreationContext = createContext<{
  state: ProgramCreationState;
  dispatch: React.Dispatch<Action>;
}>({ state: initialState, dispatch: () => {} });

export function ProgramCreationProvider({ children }: { children: ReactNode }) {
  const stored = localStorage.getItem('programCreation');
  const parsed = stored ? JSON.parse(stored) : {};
  const mergedInitialState = {
    ...initialState,
    ...parsed,
    ai: {
      ...initialState.ai,
      ...(parsed.ai || {})
    },
    masterclass: { ...initialState.masterclass, ...(parsed.masterclass || {}) },
    mentorship: { ...initialState.mentorship, ...(parsed.mentorship || {}) },
    guidedMasterclass: { ...initialState.guidedMasterclass, ...(parsed.guidedMasterclass || {}) }
  };

  const [state, dispatch] = useReducer(reducer, mergedInitialState);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    localStorage.setItem('programCreation', JSON.stringify(state));
  }, [state]);

  useEffect(() => {
    if (!state.groupId || !state.programType) return undefined;

    const payload = {
      programType: state.programType,
      masterclass: state.masterclass,
      mentorship: state.mentorship,
      guidedMasterclass: state.guidedMasterclass,
      buildMode: state.buildMode,
      ai: state.ai,
      groupId: state.groupId,
    };

    const handler = setTimeout(() => {
      API.saveBuildState({ grpId: state.groupId as string, data: payload }).catch((err) => {
        console.error('Failed to persist build state', err);
      });
    }, 500);

    return () => clearTimeout(handler);
  }, [
    state.groupId,
    state.programType,
    state.buildMode,
    state.masterclass,
    state.mentorship,
    state.guidedMasterclass,
    state.ai
  ]);

  useEffect(() => {
    if (!state.ai.contentPollingActive || !state.generationId) {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      return undefined;
    }

    let cancelled = false;

    const fetchStatus = async () => {
      try {
        const response = await API.getProgramContent({ generationId: state.generationId as string });
        if (cancelled) return;
        const payload = response?.data ?? response;
        dispatch({ type: 'SET_AI_CONTENT', payload });
        dispatch({ type: 'SET_AI_CONTENT_STATUS', payload });
        dispatch({ type: 'SET_AI_CONTENT_PROGRESS', payload: payload?.progress ?? null });

        const modules = contentResponseToManualModules(payload);
        if (modules.length) {
          dispatch({ type: 'SET_MODULES', payload: modules });
        }

        const completed =
          payload?.status === 'completed' ||
          modules.every((module) => !module.status || module.status === 'completed');

        if (completed) {
          dispatch({ type: 'SET_AI_CONTENT_LOADING', payload: false });
          dispatch({ type: 'SET_AI_CONTENT_POLLING', payload: false });
        }
      } catch (err) {
        console.error('Program content status polling error', err);
      }
    };

    fetchStatus();
    pollingRef.current = setInterval(fetchStatus, 5000);

    return () => {
      cancelled = true;
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [state.ai.contentPollingActive, state.generationId]);

  return <ProgramCreationContext.Provider value={{ state, dispatch }}>{children}</ProgramCreationContext.Provider>;
}

export function useProgramCreation() {
  return useContext(ProgramCreationContext);
}
